import {
  Activity,
  AlertTriangle,
  ArrowLeftRight,
  BookOpen,
  Braces,
  ClipboardList,
  CloudCog,
  Cpu,
  Eye,
  Flag,
  GraduationCap,
  Handshake,
  Layers,
  Lightbulb,
  ListChecks,
  MapPin,
  Monitor,
  Network,
  PiggyBank,
  ReceiptText,
  Rocket,
  Route,
  Scale,
  Shield,
  ShieldCheck,
  Star,
  TrendingUp,
  User,
  UserCheck,
  Users,
  UsersRound,
  Workflow,
  Zap,
} from "lucide-react";
import type { BubbleConfig } from "@/components/diagrams/types";

// ClipboardList / Monitor / Scale are referenced only by MC_00 in
// CapabilityRing.tsx; they're imported here to keep the icon roster
// in one place when future panels need them.
void ClipboardList;
void Monitor;
void Scale;

/**
 * All workshop panel configurations. Ported from `BUBBLE_DATA` in
 * `_legacy/xaes-fx.js` (lines 196-668) and the per-`MC_WS*` panel
 * layouts in `_legacy/mc.html`.
 *
 * Every bubble carries a `tipTitle` + `tipBody` pair that the global
 * tooltip overlay reads on hover — short, substantive captions that
 * add context the bubble can't fit inside its own ring.
 */

export type AccentKey = "true-blue" | "sky" | "gold" | "melon";

/**
 * One activity card in the workshop side panel. Ported from the
 * `right: brief + activities` blocks in `_legacy/mc.html` (per
 * `MC_WSn` panel). Status drives the card's visual treatment:
 *   - `done`    green pulse dot + green-tinted card
 *   - `active`  accent-tinted card with a stronger border + glow
 *               (only one per workshop — currently WS4 Activity 2)
 *   - `locked`  muted card with a hollow dot + lock icon
 *
 * `details` carries the dropdown body lines a card reveals when
 * expanded — WS4 is the only workshop in the legacy that ships
 * substantive copy here, the others have a single label only.
 */
export type ActivityStatus = "done" | "active" | "locked";

export type WorkshopActivity = {
  id: string;
  /** Card label, e.g. `Intro`, `Activity 1`, `Outcome`. */
  title: string;
  status: ActivityStatus;
  /** Optional expanded body lines (WS4 only in the legacy). */
  details?: string[];
};

/**
 * Normalise a workshop's activity statuses from the single source of
 * truth — the set of DONE activity ids. Mirrors
 * `normalizePhaseStatuses` (Run Sequence) one-for-one, mapped to the
 * three-state activity enum: "active" is DERIVED, never toggled, so
 * there is always exactly one active activity — the FIRST that isn't
 * done — and everything after it is "locked".
 *
 * This is what gives the Mission Control activity list its cascade:
 *   - mark an activity done  → the marker advances to the next one.
 *   - un-mark an activity    → the marker falls back to the earliest
 *     incomplete one (the one just reopened, i.e. "the previous one").
 *
 * `orderedIds` must be in panel order — that order defines "first".
 */
export function normalizeActivityStatuses(
  orderedIds: string[],
  doneIds: Set<string>,
): Record<string, ActivityStatus> {
  const out: Record<string, ActivityStatus> = {};
  let activeAssigned = false;
  for (const id of orderedIds) {
    if (doneIds.has(id)) {
      out[id] = "done";
    } else if (!activeAssigned) {
      out[id] = "active";
      activeAssigned = true;
    } else {
      out[id] = "locked";
    }
  }
  return out;
}

/**
 * The "Brief" block above the activities — short uppercase line that
 * names the workshop's headline goal (e.g. `EXECUTIVE ALIGNMENT`).
 */
export type WorkshopBrief = string;

export type WorkshopPanelData = {
  workshopId: "WS1" | "WS2" | "WS3" | "WS4" | "WS5" | "WS6" | "WS7" | "WS8";
  accent: AccentKey;
  smokeColor: [number, number, number];
  viewBox: string;
  bubbles: BubbleConfig[];
  connectors: Array<[number, number, number, number]>;
  wordmark?: {
    eyebrow: string;
    lines: string[];
    subtitle: string[];
  };
  brief: WorkshopBrief;
  activities: WorkshopActivity[];
};

const TRUE_BLUE = "var(--accent-true-blue)";
const SKY = "var(--accent-sky)";
const GOLD = "var(--accent-gold)";
const MELON = "var(--accent-melon)";
const GREEN = "var(--accent-green)";

const FOUR_2X2_CONNECTORS: Array<[number, number, number, number]> = [
  [169, 94, 311, 94],
  [169, 236, 311, 236],
  [169, 94, 169, 236],
  [311, 94, 311, 236],
  [169, 94, 311, 236],
  [311, 94, 169, 236],
];

const FIVE_CROSS_CONNECTORS: Array<[number, number, number, number]> = [
  [206, 90, 420, 90],
  [206, 270, 420, 270],
  [206, 90, 206, 270],
  [420, 90, 420, 270],
  [206, 90, 313, 180],
  [420, 90, 313, 180],
  [206, 270, 313, 180],
  [420, 270, 313, 180],
];

const WS8_ROW_CONNECTORS: Array<[number, number, number, number]> = [
  [80, 149, 250, 149],
  [250, 149, 420, 149],
  [420, 149, 590, 149],
];

const baseFour = (cx: number, cy: number) =>
  ({
    outerR: 76,
    innerR: 71,
    outerStrokeW: 1.5,
    outerOp: 0.7,
    ringStrokeW: 1,
    ringOp: 0.4,
    iconTx: cx - 9,
    iconScale: 0.75,
    titleFontSize: 10,
    letterSpacing: 0.5,
    dividerHW: 47,
    bodyFontSize: 11,
    cx,
    cy,
  }) as const;

const baseFive = baseFour;

const baseEight = (cx: number, cy: number) =>
  ({
    outerR: 62,
    innerR: 57,
    outerStrokeW: 1.5,
    outerOp: 0.7,
    ringStrokeW: 1,
    ringOp: 0.4,
    iconTx: cx - 9,
    iconScale: 0.75,
    titleFontSize: 10,
    letterSpacing: 0.5,
    dividerHW: 35,
    bodyFontSize: 11,
    cx,
    cy,
  }) as const;

export const WORKSHOP_PANEL_DATA: Record<
  WorkshopPanelData["workshopId"],
  WorkshopPanelData
> = {
  WS1: {
    workshopId: "WS1",
    accent: "true-blue",
    smokeColor: [73, 149, 255],
    viewBox: "85 -15 310 360",
    connectors: FOUR_2X2_CONNECTORS,
    wordmark: {
      eyebrow: "WS1",
      lines: ["ALIGN", "THE", "LEADERS"],
      subtitle: [
        "Set purpose, vision,",
        "outcomes & commitment",
        "for the programme.",
      ],
    },
    brief: "EXECUTIVE ALIGNMENT",
    activities: [
      { id: "ws1-intro", title: "Intro", status: "done" },
      { id: "ws1-a1", title: "Activity 1", status: "done" },
      { id: "ws1-a2", title: "Activity 2", status: "done" },
      { id: "ws1-a3", title: "Activity 3", status: "done" },
    ],
    bubbles: [
      {
        id: "bws1-tl",
        ...baseFour(169, 94),
        fill: `url(#grad-WS1)`,
        stroke: TRUE_BLUE,
        filter: `url(#glow-WS1)`,
        titleLines: ["PURPOSE"],
        bodyLines: ["Why we", "exist."],
        icon: MapPin,
        tipTitle: "Purpose",
        tipBody:
          "Why we exist — establishing the fundamental reason for the AI transformation and the human value it serves.",
      },
      {
        id: "bws1-tr",
        ...baseFour(311, 94),
        fill: `url(#grad-WS1)`,
        stroke: TRUE_BLUE,
        filter: `url(#glow-WS1)`,
        titleLines: ["VISION"],
        bodyLines: ["Where we're", "going."],
        icon: Eye,
        tipTitle: "Vision",
        tipBody:
          "Where we're going — painting a clear and compelling picture of the future state the organisation is working towards.",
      },
      {
        id: "bws1-bl",
        ...baseFour(169, 236),
        fill: `url(#grad-WS1)`,
        stroke: TRUE_BLUE,
        filter: `url(#glow-WS1)`,
        titleLines: ["OUTCOMES"],
        bodyLines: ["What success", "looks like."],
        icon: TrendingUp,
        tipTitle: "Outcomes",
        tipBody:
          "What success looks like — defining the measurable results and tangible value that will validate the transformation.",
      },
      {
        id: "bws1-br",
        ...baseFour(311, 236),
        fill: `url(#grad-WS1)`,
        stroke: TRUE_BLUE,
        filter: `url(#glow-WS1)`,
        titleLines: ["COMMITMENT"],
        bodyLines: ["How we'll work", "together."],
        icon: Handshake,
        tipTitle: "Commitment",
        tipBody:
          "How we'll work together — agreeing the behaviours, accountabilities and ways of working that will carry the transformation forward.",
      },
    ],
  },

  WS2: {
    workshopId: "WS2",
    accent: "true-blue",
    smokeColor: [73, 149, 255],
    viewBox: "85 -15 310 360",
    connectors: FOUR_2X2_CONNECTORS,
    wordmark: {
      eyebrow: "WS2",
      lines: ["MAP", "THE", "BASELINE"],
      subtitle: [
        "Pick the process to",
        "redesign. Baseline",
        "today's pain &",
        "opportunity.",
      ],
    },
    brief: "CURRENT PROCESS SELECTION & BASELINE",
    activities: [
      { id: "ws2-intro", title: "Intro", status: "done" },
      { id: "ws2-a1", title: "Activity 1", status: "done" },
      { id: "ws2-a2", title: "Activity 2", status: "done" },
      { id: "ws2-a3", title: "Activity 3", status: "done" },
    ],
    bubbles: [
      {
        id: "bws2-tl",
        ...baseFour(169, 94),
        fill: `url(#grad-WS2)`,
        stroke: TRUE_BLUE,
        filter: `url(#glow-WS2)`,
        titleLines: ["CURRENT", "STATE MAP"],
        bodyLines: ["End-to-end", "process view."],
        icon: Network,
        tipTitle: "Current State Map",
        tipBody:
          "End-to-end process view — visualising every step, handoff, system, and actor involved in how work flows today.",
      },
      {
        id: "bws2-tr",
        ...baseFour(311, 94),
        fill: `url(#grad-WS2)`,
        stroke: TRUE_BLUE,
        filter: `url(#glow-WS2)`,
        titleLines: ["PAIN POINTS", "& FRICTION"],
        bodyLines: ["Where & why", "value is lost."],
        icon: AlertTriangle,
        tipTitle: "Pain Points & Friction",
        tipBody:
          "Where & why value is lost — surfacing the delays, handoff failures, rework loops, and decision bottlenecks that drain time and quality.",
      },
      {
        id: "bws2-bl",
        ...baseFour(169, 236),
        fill: `url(#grad-WS2)`,
        stroke: TRUE_BLUE,
        filter: `url(#glow-WS2)`,
        titleLines: ["OPPORTUNITY", "INSIGHTS"],
        bodyLines: ["Prioritised", "improvement areas."],
        icon: Lightbulb,
        tipTitle: "Opportunity Insights",
        tipBody:
          "Prioritised improvement areas — ranking where AI and redesign will unlock the greatest efficiency, experience, and value gains.",
      },
      {
        id: "bws2-br",
        ...baseFour(311, 236),
        fill: `url(#grad-WS2)`,
        stroke: TRUE_BLUE,
        filter: `url(#glow-WS2)`,
        titleLines: ["PROCESS", "SELECTION"],
        bodyLines: ["Focus for", "future state design."],
        icon: ListChecks,
        tipTitle: "Process Selection",
        tipBody:
          "Focus for future state design — agreeing which process will be redesigned with AI, based on evidence of impact, readiness, and strategic fit.",
      },
    ],
  },

  WS3: {
    workshopId: "WS3",
    accent: "sky",
    smokeColor: [161, 230, 255],
    viewBox: "125 -15 380 390",
    connectors: FIVE_CROSS_CONNECTORS,
    wordmark: {
      eyebrow: "WS3",
      lines: ["DESIGN", "THE", "TARGET"],
      subtitle: [
        "Shape the AI-enhanced",
        "future-state process",
        "for value, speed,",
        "& experience.",
      ],
    },
    brief: "TO BE AI ENHANCED PROCESS DESIGN",
    activities: [
      { id: "ws3-intro", title: "Intro", status: "done" },
      { id: "ws3-a1", title: "Activity 1", status: "done" },
      { id: "ws3-a2", title: "Activity 2", status: "done" },
      { id: "ws3-a3", title: "Activity 3", status: "done" },
    ],
    bubbles: [
      {
        id: "bws3-tl",
        ...baseFive(206, 90),
        fill: `url(#grad-WS3)`,
        stroke: SKY,
        filter: `url(#glow-WS3)`,
        titleLines: ["HIGHER", "CONVERSION"],
        bodyLines: ["Remove friction,", "increase", "completion."],
        icon: TrendingUp,
        tipTitle: "Higher Conversion",
        tipBody:
          "Remove friction, increase completion — AI agents streamline every step so more work flows through without drop-off or delay.",
      },
      {
        id: "bws3-tr",
        ...baseFive(420, 90),
        fill: `url(#grad-WS3)`,
        stroke: SKY,
        filter: `url(#glow-WS3)`,
        titleLines: ["LOWER", "COST"],
        bodyLines: ["Automate routine,", "reduce", "manual work."],
        icon: PiggyBank,
        tipTitle: "Lower Cost",
        tipBody:
          "Automate routine, reduce manual work — AI agents handle repetitive tasks so people focus only on what needs human judgment.",
      },
      {
        id: "bws3-bl",
        ...baseFive(206, 270),
        fill: `url(#grad-WS3)`,
        stroke: SKY,
        filter: `url(#glow-WS3)`,
        titleLines: ["FASTER", "CYCLE TIME"],
        bodyLines: ["Real-time", "decisions, no", "hand-offs."],
        icon: Zap,
        tipTitle: "Faster Cycle Time",
        tipBody:
          "Real-time decisions, no hand-offs — AI agents act immediately, eliminating the queues and wait times that slow every process down.",
      },
      {
        id: "bws3-br",
        ...baseFive(420, 270),
        fill: `url(#grad-WS3)`,
        stroke: SKY,
        filter: `url(#glow-WS3)`,
        titleLines: ["SAFE &", "COMPLIANT"],
        bodyLines: ["Governed AI", "with built-in", "controls."],
        icon: ShieldCheck,
        tipTitle: "Safe & Compliant",
        tipBody:
          "Governed AI with built-in controls — every AI action operates within defined boundaries, fully auditable and compliant from the start.",
      },
      {
        id: "bws3-center",
        ...baseFive(313, 180),
        outerOp: 0.9,
        fill: `url(#grad-WS3)`,
        stroke: SKY,
        filter: `url(#glow-WS3)`,
        titleLines: ["BETTER", "EXPERIENCE"],
        bodyLines: ["Relevant,", "simple &", "proactive."],
        icon: Star,
        tipTitle: "Better Experience",
        tipBody:
          "Relevant, simple & proactive — AI-enhanced processes feel effortless for the people inside and outside them.",
      },
    ],
  },

  WS4: {
    workshopId: "WS4",
    accent: "sky",
    smokeColor: [161, 230, 255],
    viewBox: "115 -10 330 345",
    connectors: [
      [200, 100, 360, 100],
      [200, 100, 280, 225],
      [360, 100, 280, 225],
    ],
    wordmark: {
      eyebrow: "WS4",
      lines: ["DESIGN", "THE", "HUMAN", "SYSTEM"],
      subtitle: [
        "Design the",
        "people, roles",
        "& culture to",
        "enable trusted,",
        "lasting adoption.",
      ],
    },
    brief: "HUMAN-AGENT WAYS OF WORKING AND OPERATING MODEL",
    activities: [
      {
        id: "ws4-intro",
        title: "Intro",
        status: "done",
        details: [
          "Set Context & Objectives",
          "Introduce Workshop Structure",
        ],
      },
      {
        id: "ws4-a1",
        title: "Activity 1",
        status: "done",
        details: [
          "Define the Human Roles",
          "Map Current Ways of Working",
        ],
      },
      {
        id: "ws4-a2",
        title: "Activity 2",
        status: "active",
        details: [
          "Define the Human-Agent Handshake",
          "Stress-Test Role Play (FUD X Adoption)",
        ],
      },
      {
        id: "ws4-a3",
        title: "Activity 3",
        status: "locked",
        details: [
          "Review & Approve HAWW Blueprint",
          "Assign Responsibilities",
        ],
      },
      {
        id: "ws4-outcome",
        title: "Outcome",
        status: "locked",
        details: [
          "Consolidated Outputs",
          "Agree on Next Steps",
        ],
      },
    ],
    bubbles: [
      {
        id: "bws4-tl",
        ...baseFour(200, 100),
        fill: `url(#grad-WS4)`,
        stroke: SKY,
        filter: `url(#glow-WS4)`,
        titleLines: ["WAYS OF", "WORKING"],
        bodyLines: ["People & agents", "working together", "seamlessly."],
        icon: ArrowLeftRight,
        tipTitle: "Ways of Working",
        tipBody:
          "Defines how humans and AI agents collaborate across workflows — establishing the interaction patterns, oversight levels, and decision rights that shape daily operations.",
      },
      {
        id: "bws4-bl",
        ...baseFour(360, 100),
        fill: `url(#grad-WS4)`,
        stroke: SKY,
        filter: `url(#glow-WS4)`,
        titleLines: ["ROLES &", "WORKFORCE"],
        bodyLines: ["Empowered teams", "creating better", "outcomes."],
        icon: Users,
        tipTitle: "Roles & Workforce",
        tipBody:
          "Defines the roles, responsibilities, and workforce structures needed for humans and AI agents to collaborate effectively — including new role archetypes and capability requirements.",
      },
      {
        id: "bws4-br",
        ...baseFour(280, 225),
        fill: `url(#grad-WS4)`,
        stroke: SKY,
        filter: `url(#glow-WS4)`,
        titleLines: ["CULTURE &", "ADOPTION"],
        bodyLines: ["Shared goals,", "continuous learning", "& improvement."],
        icon: UsersRound,
        tipTitle: "Culture & Adoption",
        tipBody:
          "Builds the mindsets, behaviours, and cultural conditions that enable people to embrace AI-augmented ways of working — driving lasting adoption across the organisation.",
      },
    ],
  },

  WS5: {
    workshopId: "WS5",
    accent: "gold",
    smokeColor: [255, 174, 65],
    viewBox: "125 -15 380 390",
    connectors: FIVE_CROSS_CONNECTORS,
    wordmark: {
      eyebrow: "WS5",
      lines: ["GOVERN", "WITH", "TRUST"],
      subtitle: [
        "Audit, monitor, control",
        "& approve. Keep humans",
        "in the right loops.",
      ],
    },
    brief: "GOVERNANCE, RISK COMPLIANCE & SAFETY",
    activities: [
      { id: "ws5-intro", title: "Intro", status: "locked" },
      { id: "ws5-a1", title: "Activity 1", status: "locked" },
      { id: "ws5-a2", title: "Activity 2", status: "locked" },
      { id: "ws5-a3", title: "Activity 3", status: "locked" },
    ],
    bubbles: [
      {
        id: "bws5-tl",
        ...baseFive(206, 90),
        fill: `url(#grad-WS5)`,
        stroke: GOLD,
        filter: `url(#glow-WS5)`,
        titleLines: ["AUDIT &", "TRACEABILITY"],
        bodyLines: ["End to end visibility", "& immutable", "audit trails."],
        icon: ReceiptText,
        tipTitle: "Audit & Traceability",
        tipBody:
          "End to end visibility & immutable audit trails — every AI action logged, traceable and reviewable for full accountability.",
      },
      {
        id: "bws5-tr",
        ...baseFive(420, 90),
        fill: `url(#grad-WS5)`,
        stroke: GOLD,
        filter: `url(#glow-WS5)`,
        titleLines: ["MONITORING &", "DETECTION"],
        bodyLines: [
          "Continuous monitoring",
          "for performance,",
          "risk & anomalies.",
        ],
        icon: Activity,
        tipTitle: "Monitoring & Detection",
        tipBody:
          "Continuous monitoring for performance, risk & anomalies — real-time visibility into agent behaviour, outputs and system health.",
      },
      {
        id: "bws5-bl",
        ...baseFive(206, 270),
        fill: `url(#grad-WS5)`,
        stroke: GOLD,
        filter: `url(#glow-WS5)`,
        titleLines: ["CONTROLS &", "GUARDRAILS"],
        bodyLines: ["Built in safeguards", "within workflows", "& agents."],
        icon: Shield,
        tipTitle: "Controls & Guardrails",
        tipBody:
          "Built in safeguards within workflows & agents — preventing harmful actions and ensuring agents operate within defined boundaries.",
      },
      {
        id: "bws5-br",
        ...baseFive(420, 270),
        fill: `url(#grad-WS5)`,
        stroke: GOLD,
        filter: `url(#glow-WS5)`,
        titleLines: ["POLICIES &", "STANDARDS"],
        bodyLines: ["Clear rules for AI", "& human", "decisions."],
        icon: BookOpen,
        tipTitle: "Policies & Standards",
        tipBody:
          "Clear rules for AI and human decisions — establishing the standards, guidelines and boundaries that govern responsible AI use.",
      },
      {
        id: "bws5-center",
        ...baseFive(313, 180),
        outerOp: 0.9,
        fill: `url(#grad-WS5)`,
        stroke: GOLD,
        filter: `url(#glow-WS5)`,
        titleLines: ["APPROVALS &", "OVERSIGHT"],
        bodyLines: ["Right decisions", "escalated to the", "right people."],
        icon: UserCheck,
        tipTitle: "Approvals & Human Oversight",
        tipBody:
          "Right decisions escalated to the right people — ensuring human judgement remains central to high-stakes AI-assisted decisions.",
      },
    ],
  },

  WS6: {
    workshopId: "WS6",
    accent: "gold",
    smokeColor: [255, 174, 65],
    viewBox: "125 -15 380 390",
    connectors: FIVE_CROSS_CONNECTORS,
    wordmark: {
      eyebrow: "WS6",
      lines: ["OPERATE", "THE", "MODEL"],
      subtitle: [
        "Bring experience, data,",
        "technology, governance",
        "& process into one",
        "operating model.",
      ],
    },
    brief: "PHASE 0 BLUEPRINT DEMO",
    activities: [
      { id: "ws6-intro", title: "Intro", status: "locked" },
      { id: "ws6-a1", title: "Activity 1", status: "locked" },
      { id: "ws6-a2", title: "Activity 2", status: "locked" },
      { id: "ws6-a3", title: "Activity 3", status: "locked" },
    ],
    bubbles: [
      {
        id: "bws6-tl",
        ...baseFive(206, 90),
        fill: `url(#grad-WS6)`,
        stroke: GOLD,
        filter: `url(#glow-WS6)`,
        titleLines: ["EXPERIENCE"],
        bodyLines: ["Journeys &", "touchpoints."],
        icon: User,
        tipTitle: "Experience",
        tipBody:
          "Journeys & touchpoints — designing the human and AI interactions that define how people experience the transformed process.",
      },
      {
        id: "bws6-tr",
        ...baseFive(420, 90),
        fill: `url(#grad-WS6)`,
        stroke: GOLD,
        filter: `url(#glow-WS6)`,
        titleLines: ["GOVERNANCE"],
        bodyLines: ["Controls &", "guardrails."],
        icon: Network,
        tipTitle: "Governance",
        tipBody:
          "Controls & guardrails — embedding the policies, accountability and oversight structures that keep AI operating safely and responsibly.",
      },
      {
        id: "bws6-bl",
        ...baseFive(206, 270),
        fill: `url(#grad-WS6)`,
        stroke: GOLD,
        filter: `url(#glow-WS6)`,
        titleLines: ["DATA &", "KNOWLEDGE"],
        bodyLines: ["Context &", "intelligence."],
        icon: Route,
        tipTitle: "Data & Knowledge",
        tipBody:
          "Context & intelligence — identifying the data, knowledge bases and memory structures that power effective AI agent reasoning.",
      },
      {
        id: "bws6-br",
        ...baseFive(420, 270),
        fill: `url(#grad-WS6)`,
        stroke: GOLD,
        filter: `url(#glow-WS6)`,
        titleLines: ["TECHNOLOGY"],
        bodyLines: ["Platforms &", "integration."],
        icon: Layers,
        tipTitle: "Technology",
        tipBody:
          "Platforms & integration — selecting and connecting the tools, platforms and infrastructure that enable AI agents to operate at scale.",
      },
      {
        id: "bws6-center",
        ...baseFive(313, 180),
        outerOp: 0.9,
        fill: `url(#grad-WS6)`,
        stroke: GOLD,
        filter: `url(#glow-WS6)`,
        titleLines: ["PROCESS"],
        bodyLines: ["AI enhanced", "workflows."],
        icon: Star,
        tipTitle: "Process",
        tipBody:
          "AI enhanced workflows — redesigning end-to-end processes to integrate AI agents and unlock new levels of speed and quality.",
      },
    ],
  },

  WS7: {
    workshopId: "WS7",
    accent: "gold",
    smokeColor: [255, 174, 65],
    viewBox: "85 -15 310 360",
    connectors: FOUR_2X2_CONNECTORS,
    wordmark: {
      eyebrow: "WS7",
      lines: ["PROVE", "THE", "VALUE"],
      subtitle: [
        "Demonstrate, validate,",
        "build confidence &",
        "align on the path",
        "forward.",
      ],
    },
    brief: "EXECUTIVE SHOWCASE",
    activities: [
      { id: "ws7-intro", title: "Intro", status: "locked" },
      { id: "ws7-a1", title: "Activity 1", status: "locked" },
      { id: "ws7-a2", title: "Activity 2", status: "locked" },
      { id: "ws7-a3", title: "Activity 3", status: "locked" },
    ],
    bubbles: [
      {
        id: "bws7-tl",
        ...baseFour(169, 94),
        fill: `url(#grad-WS7)`,
        stroke: GOLD,
        filter: `url(#glow-WS7)`,
        titleLines: ["DEMONSTRATE", "VALUE"],
        bodyLines: ["Set the solution", "in action with", "real scenarios."],
        icon: Cpu,
        tipTitle: "Demonstrate Value",
        tipBody:
          "Set the solution in action with real scenarios — showing stakeholders exactly how AI-enhanced processes perform against the challenges they face.",
      },
      {
        id: "bws7-tr",
        ...baseFour(311, 94),
        fill: `url(#grad-WS7)`,
        stroke: GOLD,
        filter: `url(#glow-WS7)`,
        titleLines: ["VALIDATE", "OUTCOMES"],
        bodyLines: ["Confirm impact on", "key metrics & goals."],
        icon: Workflow,
        tipTitle: "Validate Outcomes",
        tipBody:
          "Confirm impact on key metrics & goals — measuring actual performance against the baseline to prove the value of the AI-enhanced process.",
      },
      {
        id: "bws7-bl",
        ...baseFour(169, 236),
        fill: `url(#grad-WS7)`,
        stroke: GOLD,
        filter: `url(#glow-WS7)`,
        titleLines: ["BUILD", "CONFIDENCE"],
        bodyLines: ["Transparency, trust", "& evidence-driven", "adoption."],
        icon: CloudCog,
        tipTitle: "Build Confidence",
        tipBody:
          "Transparency, trust & evidence driven adoption — giving stakeholders the visibility and understanding they need to commit to scaling the solution.",
      },
      {
        id: "bws7-br",
        ...baseFour(311, 236),
        fill: `url(#grad-WS7)`,
        stroke: GOLD,
        filter: `url(#glow-WS7)`,
        titleLines: ["ALIGN ON", "NEXT STEPS"],
        bodyLines: ["Agree on priorities", "& the path forward."],
        icon: Braces,
        tipTitle: "Align on Next Steps",
        tipBody:
          "Agree on priorities & the path forward — closing the showcase with clear decisions, ownership and momentum for what comes next.",
      },
    ],
  },

  WS8: {
    workshopId: "WS8",
    accent: "melon",
    smokeColor: [255, 126, 81],
    viewBox: "10 80 650 140",
    connectors: WS8_ROW_CONNECTORS,
    wordmark: {
      eyebrow: "PREPARE",
      lines: ["FLIGHT", "PLAN"],
      subtitle: [
        "Phase 0 → Phase 1 →",
        "Phase 2+ → Beyond.",
        "Sustainable impact.",
        "Future-ready.",
      ],
    },
    brief: "FLIGHT PLAN",
    activities: [
      { id: "ws8-p0", title: "Phase 0", status: "locked" },
      { id: "ws8-p1", title: "Phase 1", status: "locked" },
      { id: "ws8-p2", title: "Phase 2+", status: "locked" },
      { id: "ws8-beyond", title: "Beyond", status: "locked" },
    ],
    bubbles: [
      {
        id: "bws8-tl",
        ...baseEight(80, 149),
        fill: `url(#grad-WS8)`,
        stroke: MELON,
        filter: `url(#glow-WS8)`,
        titleLines: ["PHASE 0"],
        bodyLines: ["Align, Design", "& Validate."],
        icon: Rocket,
        tipTitle: "Phase 0",
        tipBody:
          "Align, Design & Validate — establish the foundation: align stakeholders, design the AI-enhanced processes, and validate the blueprint before committing to full implementation.",
        label: {
          title: "OUR DIRECTION",
          bodyLines: ["Outcome-driven.", "People-centred."],
        },
      },
      {
        id: "bws8-tr",
        ...baseEight(250, 149),
        fill: `url(#grad-WS8)`,
        stroke: MELON,
        filter: `url(#glow-WS8)`,
        titleLines: ["PHASE 1"],
        bodyLines: ["Prepare, Implement", "& Adapt."],
        icon: Activity,
        tipTitle: "Phase 1",
        tipBody:
          "Prepare, Implement & Adapt — move from blueprint to reality: prepare the organisation, implement the solution, and adapt based on early learnings.",
        label: {
          title: "OUR APPROACH",
          bodyLines: ["Iterate early.", "Learn fast."],
        },
      },
      {
        id: "bws8-bl",
        ...baseEight(420, 149),
        fill: `url(#grad-WS8)`,
        stroke: MELON,
        filter: `url(#glow-WS8)`,
        titleLines: ["PHASE 2+"],
        bodyLines: ["Adopt, Scale", "& Transform"],
        icon: Flag,
        tipTitle: "Phase 2+",
        tipBody:
          "Adopt, Scale & Transform — build on early wins: drive broad adoption, scale across the organisation, and transform how work gets done at every level.",
        label: {
          title: "OUR COMMITMENT",
          bodyLines: ["Deliver value.", "Build confidence."],
        },
      },
      {
        id: "bws8-br",
        ...baseEight(590, 149),
        fill: `url(#grad-WS8-green)`,
        stroke: GREEN,
        filter: `url(#glow-WS8)`,
        titleLines: ["BEYOND"],
        bodyLines: ["Sustain, Innovate", "& Evolve"],
        icon: GraduationCap,
        tipTitle: "Beyond",
        tipBody:
          "Sustain, Innovate & Evolve — lock in the gains and keep moving: sustain what works, innovate continuously, and evolve the organisation as AI capabilities advance.",
        label: {
          title: "OUR DESTINATION",
          color: GREEN,
          bodyLines: ["Sustainable impact.", "Future ready."],
        },
      },
    ],
  },
};
